// The GM boundary's pre-draw dry run (gm-boundary-truth.ts resolve()): every PAYLOAD_DRY_PATTERNS face before the real draw, so a
// trigger consent is asked before any die. The patterns differ ONLY in their roll source. A pattern that completes cleanly without
// drawing a die can therefore not reach a branch another pattern would: the other eight are skipped. A GM status add on a 16-creature
// encounter spent ~30 whole-encounter deep clones (~3 s of main thread) proving nothing new (owner playtest 2026-10-14).
// "Cleanly" = no skip row (no frame handed back inside) and no new consent request. The dry runs also share ONE uuid sequence and ONE
// campaign-clock reading, so nothing but the roll source can differ between two patterns.
import { PAYLOAD_DRY_PATTERNS, payloadDryRunRollSource } from '../ability/environmental-payload.js';
import { recordingRollSource, type RollSource } from '../actions/roll-source.js';

export interface DryRunReaders { uuid: () => string; campaignClock: () => unknown }

/** One env for every dry pattern: the i-th uuid and the clock read are the same in each. */
export function pinnedDryEnv(): <R extends DryRunReaders>(read: R) => R {
  const ids: string[] = [];
  let clock: { value: unknown } | null = null;
  return <R extends DryRunReaders>(read: R): R => {
    let next = 0;
    return { ...read,
      uuid: () => { const i = next++; return ids[i] ??= read.uuid(); },
      campaignClock: () => {
        clock ??= { value: read.campaignClock() };
        return clock.value && typeof clock.value === 'object' ? { ...clock.value } : clock.value;
      } } as R;
  };
}

/** Runs the dry patterns in order; stops early only when the FIRST is clean and draw-free (an inconclusive first explores all nine).
 *  Returns how many ran. A throw propagates. */
export function exploreDryPatterns(run: (roll: RollSource) => { rows: readonly unknown[] }, consentPending: () => number): number {
  let runs = 0;
  for (const pattern of PAYLOAD_DRY_PATTERNS) {
    const roll = recordingRollSource(payloadDryRunRollSource(pattern)), asked = consentPending();
    const verdict = run(roll);
    runs += 1;
    const skipped = verdict.rows.some(row => (row as { roll_type_1?: unknown } | null)?.roll_type_1 === 'janus_unported_skip');
    if (runs === 1 && roll.draws.length === 0 && !skipped && consentPending() === asked) break;
  }
  return runs;
}
